import type { Video } from '@blackblack/shared';
import { api, errorMessage } from '../../lib/api';
import { patchEntity } from '../../lib/entities';
import { useAuth } from '../../state/auth';
import { useToast } from '../../state/toast';

/** Optimistic like / save / follow / share actions for a video, shared by feed & grids. */
export function useVideoActions(video: Video) {
  const { requireAuth, me } = useAuth();
  const toast = useToast();

  const like = async (force?: boolean) => {
    if (!requireAuth('Log in om video’s te liken')) return;
    const next = force ?? !video.viewer.liked;
    if (next === video.viewer.liked) return;
    patchEntity('video', video.id, { liked: next, likes: video.stats.likes + (next ? 1 : -1) });
    try {
      const r = next ? await api.post<{ likeCount: number }>(`/videos/${video.id}/like`) : await api.delete<{ likeCount: number }>(`/videos/${video.id}/like`);
      patchEntity('video', video.id, { liked: next, likes: Math.max(r.likeCount, video.stats.likes + (next ? 1 : -1)) });
    } catch (e) {
      patchEntity('video', video.id, { liked: !next, likes: video.stats.likes });
      toast(errorMessage(e), { tone: 'error' });
    }
  };

  const save = async () => {
    if (!requireAuth('Log in om video’s op te slaan')) return;
    const next = !video.viewer.saved;
    patchEntity('video', video.id, { saved: next, saves: video.stats.saves + (next ? 1 : -1) });
    try {
      if (next) await api.post(`/videos/${video.id}/save`);
      else await api.delete(`/videos/${video.id}/save`);
      toast(next ? 'Opgeslagen in je collectie' : 'Verwijderd uit opgeslagen');
    } catch (e) {
      patchEntity('video', video.id, { saved: !next, saves: video.stats.saves });
      toast(errorMessage(e), { tone: 'error' });
    }
  };

  const follow = async () => {
    if (!requireAuth('Log in om creators te volgen')) return;
    const next = !video.viewer.followingAuthor;
    patchEntity('user', video.author.id, { following: next });
    try {
      if (next) await api.post(`/users/${video.author.id}/follow`);
      else await api.delete(`/users/${video.author.id}/follow`);
    } catch (e) {
      patchEntity('user', video.author.id, { following: !next });
      toast(errorMessage(e), { tone: 'error' });
    }
  };

  const share = async () => {
    const url = `${location.origin}/v/${video.id}`;
    let channel: 'native' | 'link' = 'link';
    try {
      if (navigator.share) {
        await navigator.share({ title: `${video.author.displayName} op BlackBlack`, text: video.caption.slice(0, 100), url });
        channel = 'native';
      } else {
        await navigator.clipboard.writeText(url);
        toast('Link gekopieerd');
      }
    } catch {
      return; // user cancelled the share sheet
    }
    patchEntity('video', video.id, { shares: video.stats.shares + 1 });
    void api.post(`/videos/${video.id}/share`, { channel }).catch(() => {});
  };

  return { like, save, follow, share, isOwn: me?.id === video.author.id };
}
